import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Frown, Search, Loader } from 'lucide-react'; // CORREGIDO: Loader en lugar de LoaderCircle
import toast from 'react-hot-toast';

// --- ¡IMPORTANTE! ---
// Reemplaza esta URL con la URL de tu backend en Railway.
const API_URL = 'https://tienda-production-efd6.up.railway.app/api/products';


const SearchResultsPage = () => {
  // Hook para leer los parámetros de la URL (ej: ?q=camiseta)
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Función para obtener todos los productos desde tu API
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error('No se pudo conectar con el servidor.');
        }
        const allProducts = await response.json();

        // Filtrar los productos según la consulta de búsqueda
        if (query) {
          const results = allProducts.filter(product =>
            product.name.toLowerCase().includes(query.toLowerCase()) ||
            product.category.toLowerCase().includes(query.toLowerCase())
          );
          setFilteredProducts(results);
        } else {
          setFilteredProducts([]); // No mostrar nada si no hay consulta
        }

      } catch (err) {
        setError(err.message);
        toast.error(`Error: ${err.message}`);
        console.error("Error fetching products:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [query]); // Se ejecuta cada vez que la consulta de búsqueda (query) cambia

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  // --- Renderizado del componente ---

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 text-gray-600">
        <Loader className="w-12 h-12 animate-spin text-red-600" /> {/* CORREGIDO: Loader */}
        <p className="mt-4 text-lg">Buscando productos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 px-6 bg-red-50 rounded-lg shadow-md">
        <Frown className="w-16 h-16 mx-auto text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-red-800">Ocurrió un error</h2>
        <p className="text-red-700 mt-2">{error}</p>
        <p className="text-gray-600 mt-2">Por favor, intenta de nuevo más tarde.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto px-4 py-8"
    >
      <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
        <Search className="w-8 h-8 mr-3 text-red-600"/>
        Resultados de la búsqueda
      </h1>
      {query && (
        <p className="text-lg text-gray-600 mb-8">
          Para: <span className="font-semibold text-red-700">"{query}"</span>
        </p>
      )}

      {filteredProducts.length > 0 ? (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredProducts.map(product => (
            <motion.div key={product.id} variants={itemVariants}>
              <Link to={`/product/${product.id}`} className="group block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="relative">
                  <img
                    // Tu API devuelve un array de imágenes, usamos la primera.
                    // Las imágenes son base64, por lo que se pueden usar directamente.
                    src={product.images[0] || 'https://placehold.co/400x400/cccccc/ffffff?text=Sin+Imagen'}
                    alt={`Imagen de ${product.name}`}
                    className="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-2 py-1 m-2 rounded-full">
                    {product.category}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 truncate group-hover:text-red-600 transition-colors">{product.name}</h3>
                  <p className="text-xl font-bold text-red-700 mt-2">${product.price.toLocaleString('es-AR')}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16 px-6 bg-white rounded-lg shadow-md"
        >
          <Frown className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">No se encontraron resultados</h2>
          <p className="text-gray-600 mt-2">
            Intenta buscar con otros términos o explora nuestras categorías.
          </p>
          <Link to="/" className="mt-6 inline-block bg-red-600 text-white font-bold py-2 px-6 rounded-full hover:bg-red-700 transition-colors">
            Volver al inicio
          </Link>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SearchResultsPage;
